import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { rgpdClient } from '../lib/rgpd';
import { logger } from '../lib/logger';
import { analyticsService } from '../lib/analytics/analyticsService';

interface CookiePreferences {
  analytics: boolean;
  marketing?: boolean;
  preferences?: boolean;
  essential?: boolean;
}

interface CookieConsent {
  accepted: boolean;
  preferences: CookiePreferences;
  lastUpdated: string;
}

const COOKIE_CONSENT_KEY = 'cookie_consent';
const COOKIE_BANNER_DISMISSED_KEY = 'cookie_banner_dismissed';

export const useCookieConsent = () => {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [showBanner, setShowBanner] = useState<boolean | null>(null);

  // Charger la configuration RGPD
  const { data: rgpdConfig, isLoading, error } = useQuery({
    queryKey: ['rgpd-config'],
    queryFn: () => rgpdClient.getConfig(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
  });

  // Initialiser l'état du consentement une seule fois au montage
  useEffect(() => {
    if (showBanner === null) { // Seulement lors de l'initialisation
      try {
        // Check if banner was dismissed without consent
        const bannerDismissed = localStorage.getItem(COOKIE_BANNER_DISMISSED_KEY);
        if (bannerDismissed === 'true') {
          setShowBanner(false);
          return;
        }
        
        // Check for stored consent
        const storedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (storedConsent) {
          const parsedConsent = JSON.parse(storedConsent);
          setConsent(parsedConsent);
          setShowBanner(false);
          
          // Apply consent settings
          if (parsedConsent.preferences.analytics === false) {
            analyticsService.disableAnalytics();
          } else {
            analyticsService.enableAnalytics();
          }
          
          logger.debug('Loaded stored cookie consent', {
            category: 'RGPD',
            data: { consent: parsedConsent }
          });
        } else {
          setShowBanner(true);
          
          // Default to disabled analytics until consent is given
          analyticsService.disableAnalytics();
          
          logger.debug('No stored cookie consent found', {
            category: 'RGPD'
          });
        }
      } catch (error) {
        logger.error('Error loading cookie consent', {
          category: 'RGPD',
          data: { error }
        });
        setShowBanner(true);
        analyticsService.disableAnalytics();
      }
    }
  }, []); // Exécuté une seule fois au montage

  // Sauvegarder le consentement
  const saveConsent = useCallback((newConsent: CookieConsent) => {
    try {
      const consentToSave = {
        ...newConsent,
        lastUpdated: new Date().toISOString()
      };

      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentToSave));
      setConsent(consentToSave);
      setShowBanner(false);
      
      // Apply consent settings
      if (consentToSave.preferences.analytics === false) {
        analyticsService.disableAnalytics();
      } else {
        analyticsService.enableAnalytics();
      }

      logger.debug('Cookie consent saved', {
        category: 'RGPD',
        data: { consent: consentToSave }
      });
      
      // Track consent event if analytics is enabled
      if (consentToSave.preferences.analytics) {
        analyticsService.trackEvent({
          event_type: 'user',
          event_name: 'cookie_consent',
          event_data: {
            consent_type: consentToSave.accepted ? 'accept' : 'reject',
            analytics: consentToSave.preferences.analytics,
            marketing: consentToSave.preferences.marketing,
            preferences: consentToSave.preferences.preferences,
            timestamp: new Date().toISOString()
          }
        });
      }
    } catch (error) {
      logger.error('Error saving cookie consent', {
        category: 'RGPD',
        data: { error }
      });
    }
  }, []);

  // Accepter tous les cookies
  const acceptAll = useCallback(() => {
    saveConsent({
      accepted: true,
      preferences: { 
        analytics: true,
        marketing: true,
        preferences: true,
        essential: true
      },
      lastUpdated: new Date().toISOString()
    });
  }, [saveConsent]);

  // Refuser tous les cookies
  const rejectAll = useCallback(() => {
    saveConsent({
      accepted: false,
      preferences: { 
        analytics: false,
        marketing: false,
        preferences: false,
        essential: true
      },
      lastUpdated: new Date().toISOString()
    });
  }, [saveConsent]);

  // Sauvegarder les préférences
  const savePreferences = useCallback((preferences: CookiePreferences) => {
    saveConsent({
      accepted: true,
      preferences: {
        ...preferences,
        essential: true // Essential cookies are always enabled
      },
      lastUpdated: new Date().toISOString()
    });
  }, [saveConsent]);

  // Réinitialiser le consentement
  const resetConsent = useCallback(() => {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    localStorage.removeItem(COOKIE_BANNER_DISMISSED_KEY);
    setConsent(null);
    setShowBanner(true);
    analyticsService.disableAnalytics();

    logger.debug('Cookie consent reset', {
      category: 'RGPD'
    });
  }, []);

  // Dismiss banner without setting consent
  const dismissBanner = useCallback(() => {
    localStorage.setItem(COOKIE_BANNER_DISMISSED_KEY, 'true');
    setShowBanner(false);
    
    logger.debug('Cookie banner dismissed without consent', {
      category: 'RGPD'
    });
  }, []);

  return {
    consent,
    showBanner: showBanner === true, // Convertir null en false
    acceptAll,
    rejectAll,
    savePreferences,
    resetConsent,
    dismissBanner,
    rgpdConfig,
    isLoading
  };
};